import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import * as colaApi from '@/api/cola.api';
import { hora12, fechaCorta } from '@/lib/athena';

/**
 * MÉTRICAS — el WhatsApp del negocio, en números que sirven para operar.
 *
 * Esteban, 22-sep-2026, sobre RESIST: *"ir midiendo a las personas que me
 * responden, sacando análisis y métricas que me sirvan para las operaciones"*.
 *
 * ═══ EL ORDEN ═══
 *
 *   1. Quién sigue esperando — lo único de esta pantalla que pide hacer algo.
 *   2. Los números de la semana.
 *   3. Día por día.
 *
 * "Sigue esperando" va primero porque es una lista de personas, no un número:
 * un 92% de respuesta no dice a QUIÉN le falta la suya.
 *
 * ═══ LO QUE NO MIDE ═══
 *
 * Registros, cupos y asistencia viven en RESIST. Se dice abajo, para que nadie
 * compare "personas que escribieron" con "registrados" y crea que falta gente.
 */

function Numero({ titulo, valor, pie }: { titulo: string; valor: string; pie?: string }) {
  return (
    <Card>
      <CardBody className="space-y-0.5">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{titulo}</p>
        <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-50">{valor}</p>
        {pie && <p className="text-xs text-slate-500 dark:text-slate-400">{pie}</p>}
      </CardBody>
    </Card>
  );
}

/** "hace 3 h", "hace 2 días". Para "esperando", lo que importa es cuánto. */
function haceCuanto(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
}

function minutos(m: number | null): string {
  if (m === null) return '—';
  if (m < 1) return 'menos de 1 min';
  if (m < 60) return `${Math.round(m)} min`;
  return `${(m / 60).toFixed(1)} h`;
}

export default function Metricas() {
  const navegar = useNavigate();
  const q = useQuery({
    queryKey: ['metricas-whatsapp'],
    queryFn: colaApi.getMetricas,
    // Cada minuto: es la pantalla de una noche de evento, se deja abierta.
    refetchInterval: 60_000,
  });

  if (q.isPending) return <Spinner />;
  if (q.isError || !q.data) {
    return <p className="text-sm text-slate-500">No se pudieron leer las métricas.</p>;
  }
  const r = q.data;

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Métricas</h1>
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
          Tu WhatsApp, últimos {r.dias} días. Se actualiza solo cada minuto.
        </p>
      </div>

      {/* ── 1. QUIÉN SIGUE ESPERANDO ── */}
      <Card>
        <CardBody className="space-y-2">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {r.esperando.length === 0
              ? 'Nadie está esperando respuesta.'
              : `${r.esperando.length === 10 ? '10 o más personas' : r.esperando.length === 1 ? '1 persona' : `${r.esperando.length} personas`} esperando respuesta`}
          </p>
          {r.esperando.map((e) => (
            <button
              key={e.conversationId}
              onClick={() => navegar(`/conversations/${e.conversationId}`)}
              className="flex min-h-[44px] w-full items-center justify-between rounded-lg px-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <span className="truncate text-slate-800 dark:text-slate-100">{e.quien}</span>
              <span className="shrink-0 pl-3 text-xs text-slate-500">{haceCuanto(e.desde)}</span>
            </button>
          ))}
        </CardBody>
      </Card>

      {/* ── 2. LA SEMANA ── */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Numero titulo="Personas que escribieron" valor={String(r.personasQueEscribieron)} />
        <Numero
          titulo="Les contestamos a"
          valor={r.tasaDeRespuesta === null ? '—' : `${Math.round(r.tasaDeRespuesta * 100)}%`}
          pie={r.tasaDeRespuesta === null ? 'Nadie escribió todavía' : undefined}
        />
        <Numero
          titulo="Tardamos en contestar"
          valor={minutos(r.minutosMedianosDeRespuesta)}
          pie="la mitad de las veces, menos que esto"
        />
        <Numero titulo="Pidieron su pase" valor={String(r.pidieronPase)} />
        <Numero titulo="Mensajes que llegaron" valor={String(r.mensajesEntrantes)} />
        <Numero titulo="Respuestas enviadas" valor={String(r.respuestasEnviadas)} />
        <Numero
          titulo="Envíos fallidos"
          valor={String(r.enviosFallidos)}
          pie={r.enviosFallidos > 0 ? 'Míralos en la cola de envío' : undefined}
        />
        <Numero titulo="Se dieron de baja" valor={String(r.bajas)} />
      </div>

      {/* ── 3. DÍA POR DÍA ── */}
      <Card>
        <CardBody>
          <p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
            Día por día, últimos 14
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="py-1 pr-4 font-medium">Día</th>
                  <th className="py-1 pr-4 text-right font-medium">Llegaron</th>
                  <th className="py-1 text-right font-medium">Contestamos</th>
                </tr>
              </thead>
              <tbody>
                {[...r.porDia].reverse().map((d) => (
                  <tr key={d.dia} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1.5 pr-4 text-slate-700 first-letter:uppercase dark:text-slate-200">
                      {fechaCorta(`${d.dia}T12:00:00`)}
                    </td>
                    <td className="py-1.5 pr-4 text-right text-slate-800 dark:text-slate-100">{d.entrantes}</td>
                    <td className="py-1.5 text-right text-slate-800 dark:text-slate-100">{d.respuestas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Aquí se mide solo lo que pasa por WhatsApp. Los registros, los cupos y la asistencia están en
        el panel de RESIST. Actualizado a las {hora12(new Date().toISOString())}.
      </p>
    </div>
  );
}
